import { dealNetworkService, type TCompanyRecommendation } from "@/api/dealNetwork";
import { SectionCard } from "@/components/dashboard/SectionCard";
import { SectionCardChatInput } from "@/components/dashboard/SectionCardChatInput";
import { SectionCardSkeleton } from "@/components/dashboard/SectionCardSkeleton";
import { AiAssistedDisclaimer } from "@/components/shared";
import { Button, Select, Tooltip, TooltipContent, TooltipTrigger, useToast } from "@/components/ui";
import { Input } from "@/components/ui/Input";
import { AlertCircle, ChevronLeft, ChevronRight, Globe, Info, LoaderCircle, Search, User } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { extractUsageSummary, formatUsageCost, formatUsageTokens } from "@/utils/aiUsage";
import { getIntroductionStatusDescription, getIntroductionStatusMessage } from "@/utils/connectRequestStatus";
import { extractErrorMessage } from "@/utils/errorUtils";
import {
  DEFAULT_DEAL_NETWORK_RECOMMENDATION_FILTER,
  type TDealNetworkRecommendationFilter,
} from "./dealNetworkFilters";

type TMyDealNetworkFeatureProps = {
  companyId?: string | null;
  sectionId?: string | null;
  companyName?: string | null;
  recommendationFilter?: TDealNetworkRecommendationFilter;
};

type TCompanyRecommendationCardProps = {
  recommendation: TCompanyRecommendation;
  companyId: string | null;
  sectionId: string | null;
  onToggleShortlist: (recommendation: TCompanyRecommendation) => void;
  onToggleDismiss: (recommendation: TCompanyRecommendation) => void;
  onRequestIntroduction?: (recommendation: TCompanyRecommendation) => void;
  isShortlistLoading?: boolean;
  isDismissLoading?: boolean;
  isRequestLoading?: boolean;
};

function CompanyRecommendationCard({
  recommendation,
  companyId,
  sectionId,
  onToggleShortlist,
  onToggleDismiss,
  onRequestIntroduction,
  isShortlistLoading = false,
  isDismissLoading = false,
  isRequestLoading = false,
}: TCompanyRecommendationCardProps) {
  const [isAssessmentExpanded, setIsAssessmentExpanded] = useState(false);
  const investorCompany = recommendation.investor_company;
  const platformInvestor = recommendation.investor_platform_investor;
  const platform = platformInvestor?.platform;
  const isInternalInvestor = Boolean(recommendation.companies_investor_id);
  const introductionStatusMessage = getIntroductionStatusMessage(
    recommendation.connect_request?.status?.name
  );
  const introductionStatusDescription = getIntroductionStatusDescription(
    recommendation.connect_request?.status?.name
  );
  const connectStatusName = recommendation.connect_request?.status?.name?.toLowerCase();
  const showRequestIntroductionButton = isInternalInvestor && !recommendation.connect_request;
  // "Retry" only makes sense after a declined request — a pending request is
  // in flight and re-sending it reads as if something failed.
  const showRetryButton = isInternalInvestor && connectStatusName === "dismissed";

  const hasCategories = recommendation.section_categories.length > 0;
  const showPlatformDetails = Boolean(platformInvestor && platform);

  const renderExternalLink = (url: string | null | undefined, label: string) => {
    if (!url) return null;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs font-semibold text-primary hover:underline"
      >
        {label}
      </a>
    );
  };

  const contactName =
    platformInvestor?.contact_name ?? platformInvestor?.company_name ?? investorCompany?.name ?? "Investor";
  const companyNameRaw = platformInvestor?.company_name ?? investorCompany?.name ?? "";
  const companyName = companyNameRaw && companyNameRaw !== contactName ? companyNameRaw : "";
  const scoreValue = typeof recommendation.score === "number" ? recommendation.score : null;

  const poweredByLabel = (() => {
    if (platform?.name) return platform.name;
    try {
      if (platform?.website) return new URL(platform.website).hostname;
      if (platformInvestor?.website) return new URL(platformInvestor.website).hostname;
    } catch {
      /* ignore */
    }
    return null;
  })();
  const poweredByUrl = platform?.website ?? platformInvestor?.website ?? null;

  const scoreBadgeStyle =
    scoreValue != null
      ? scoreValue >= 70
        ? "border-2 border-green-500 bg-card text-foreground"
        : "border-2 border-amber-500 bg-card text-foreground"
      : "border border-border bg-card text-muted-foreground";

  return (
    <SectionCard
      headerLeft={
        <div className="flex min-w-0 flex-1 items-start gap-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600 sm:h-12 sm:w-12">
            <User className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-base font-semibold text-foreground sm:text-lg">{contactName}</p>
            {companyName ? <p className="text-sm text-muted-foreground">{companyName}</p> : null}
          </div>
        </div>
      }
      headerRight={
        <div
          className={`flex h-12 w-12 flex-shrink-0 flex-col items-center justify-center rounded-full sm:h-14 sm:w-14 ${scoreBadgeStyle}`}
        >
          {scoreValue != null ? (
            <>
              <span className="text-sm font-bold leading-none">{scoreValue}</span>
              <span className="text-[10px] leading-none text-muted-foreground">/100</span>
            </>
          ) : (
            <span className="text-sm font-bold">–</span>
          )}
        </div>
      }
      actions={
        <>
          {showRequestIntroductionButton ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  onClick={() => onRequestIntroduction?.(recommendation)}
                  disabled={isRequestLoading || isShortlistLoading || isDismissLoading}
                >
                  {isRequestLoading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Request an introduction
                  <Info className="ml-1.5 h-3.5 w-3.5 opacity-70" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Sends an introduction request to this investor. If they accept, you'll both receive an
                introduction email and an invite to a shared WhatsApp group to start the conversation.
              </TooltipContent>
            </Tooltip>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggleShortlist(recommendation)}
            disabled={isShortlistLoading || isDismissLoading}
          >
            {isShortlistLoading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
            {recommendation.shortlisted ? "Remove from shortlist" : "Shortlist"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggleDismiss(recommendation)}
            disabled={isShortlistLoading || isDismissLoading}
            className="border-border text-foreground hover:bg-muted hover:text-foreground"
          >
            {isDismissLoading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
            {recommendation.dismissed ? "Undo dismiss" : "Dismiss"}
          </Button>
        </>
      }
      footer={
        companyId && sectionId ? (
          <SectionCardChatInput
            mode="thread"
            companyId={companyId}
            sectionId={sectionId}
            titlePrefix={contactName || companyName}
            placeholder="Ask a question..."
          />
        ) : null
      }
    >
      {(recommendation.shortlisted || recommendation.dismissed) && (
        <div className="mb-2 flex flex-wrap gap-2">
          {recommendation.shortlisted ? (
            <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
              Shortlisted
            </span>
          ) : null}
          {recommendation.dismissed ? (
            <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
              Dismissed
            </span>
          ) : null}
        </div>
      )}

      {recommendation.assessment ? (
        <>
          <AiAssistedDisclaimer
            variant="compact"
            context="fundraising"
            className="mb-2"
          />
          <p className={`text-sm leading-relaxed text-foreground ${isAssessmentExpanded ? "" : "line-clamp-3"}`}>
            {recommendation.assessment}
          </p>
          {recommendation.assessment.length > 220 ? (
            <button
              type="button"
              onClick={() => setIsAssessmentExpanded((prev) => !prev)}
              className="mt-1 text-xs font-semibold text-primary hover:underline"
            >
              {isAssessmentExpanded ? "Show less" : "Show more"}
            </button>
          ) : null}
        </>
      ) : null}

      {poweredByLabel ? (
        <div className="mt-2 flex items-center gap-1.5">
          <Globe className="h-3.5 w-3.5 text-muted-foreground" />
          {poweredByUrl ? (
            <a
              href={poweredByUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold text-primary hover:underline"
            >
              Powered by {poweredByLabel}
            </a>
          ) : (
            <span className="text-xs font-semibold text-muted-foreground">Powered by {poweredByLabel}</span>
          )}
        </div>
      ) : null}

      {/* <div className="mt-4 rounded-lg border border-border bg-muted p-4">
        <p className="text-xs font-semibold text-muted-foreground">Details</p>
        {platformInvestor ? (
          <div className="mt-2 space-y-2">
            {platformInvestor.website ? (
              <p className="text-sm text-foreground">
                Website:{" "}
                <a
                  href={platformInvestor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Visit site
                </a>
              </p>
            ) : null}
            {platformInvestor.contact_us_link ? (
              <p className="text-sm text-foreground">
                Contact:{" "}
                <a
                  href={platformInvestor.contact_us_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Reach out
                </a>
              </p>
            ) : null}
          </div>
        ) : investorCompany ? (
          <div className="mt-2 text-sm text-foreground">Existing Fundy investor match with {investorCompany.name}</div>
        ) : (
          <div className="mt-2 text-sm text-foreground">No additional investor details provided.</div>
        )}
      </div> */}

      {showPlatformDetails ? (
        <div className="mt-4 rounded-lg border border-purple-100 bg-purple-50 p-4">
          <p className="text-xs font-semibold text-purple-700">Sourced via {platform?.name}</p>
          <div className="mt-2 flex flex-wrap gap-4">
            {renderExternalLink(platform?.website, "Platform Website")}
            {renderExternalLink(platform?.contact_us_link, "Contact Platform")}
          </div>
        </div>
      ) : null}

      {hasCategories ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {recommendation.section_categories.map((category) => (
            <span
              key={category.id}
              className="rounded-full bg-primary/10 px-4 py-2 text-xs font-medium text-primary"
            >
              {category.name}
            </span>
          ))}
        </div>
      ) : null}

      {introductionStatusMessage ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/20 bg-primary/10 p-2">
          <div className="min-w-0 flex-1">
            <span className="text-sm font-semibold text-primary">{introductionStatusMessage}</span>
            {introductionStatusDescription ? (
              <p className="mt-1 text-xs text-muted-foreground">{introductionStatusDescription}</p>
            ) : null}
          </div>
          {showRetryButton && onRequestIntroduction ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRequestIntroduction(recommendation)}
              disabled={isRequestLoading || isShortlistLoading || isDismissLoading}
              className="border-primary/30 text-primary hover:bg-primary/10"
            >
              {isRequestLoading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
              Retry
            </Button>
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}

const PAGE_SIZE = 5;

type TRecommendationSort = "score" | "name";

export function MyDealNetworkFeature({ companyId, sectionId, recommendationFilter }: TMyDealNetworkFeatureProps) {
  const [companyRecommendations, setCompanyRecommendations] = useState<TCompanyRecommendation[]>([]);
  const [isCompanyRecommendationsLoading, setIsCompanyRecommendationsLoading] = useState(false);
  const [companyRecommendationsError, setCompanyRecommendationsError] = useState<string | null>(null);
  const [shortlistLoadingState, setShortlistLoadingState] = useState<Record<string, boolean>>({});
  const [dismissLoadingState, setDismissLoadingState] = useState<Record<string, boolean>>({});
  const [connectRequestLoadingState, setConnectRequestLoadingState] = useState<Record<string, boolean>>({});
  const inFlightConnectRequests = useRef<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<TRecommendationSort>("score");
  const [page, setPage] = useState(1);
  const { toast } = useToast();
  const activeRecommendationFilter = recommendationFilter ?? DEFAULT_DEAL_NETWORK_RECOMMENDATION_FILTER;

  useEffect(() => {
    if (!companyId) {
      setCompanyRecommendations([]);
      setCompanyRecommendationsError(null);
      setIsCompanyRecommendationsLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchCompanyRecommendations = async () => {
      setIsCompanyRecommendationsLoading(true);
      setCompanyRecommendationsError(null);

      const { data, error: fetchError } = await dealNetworkService.getCompanyRecommendations(companyId, {
        timeout: 30_000,
      });

      if (isCancelled) {
        return;
      }

      if (fetchError) {
        const message = await extractErrorMessage(fetchError);
        setCompanyRecommendationsError(message || "Unable to load recommendations right now.");
        setCompanyRecommendations([]);
        setIsCompanyRecommendationsLoading(false);
        return;
      }

      setCompanyRecommendations(data?.data ?? []);
      setIsCompanyRecommendationsLoading(false);
    };

    void fetchCompanyRecommendations();

    return () => {
      isCancelled = true;
    };
  }, [companyId]);

  const handleToggleShortlist = useCallback(
    (recommendation: TCompanyRecommendation) => {
      setShortlistLoadingState((prev) => ({ ...prev, [recommendation.id]: true }));

      void (async () => {
        try {
          // Shortlisting a dismissed recommendation restores it — the two
          // states are contradictory and must never both be true.
          const nextShortlisted = !recommendation.shortlisted;
          const { data, error } = await dealNetworkService.updateCompanyRecommendation(recommendation.id, {
            shortlisted: nextShortlisted,
            dismissed: nextShortlisted ? false : recommendation.dismissed,
          });

          if (error) {
            const message = await extractErrorMessage(error);
            toast({
              title: "Unable to update recommendation",
              description: message,
              variant: "destructive",
            });
            return;
          }

          if (data?.data) {
            setCompanyRecommendations((prev) => prev.map((item) => (item.id === data.data.id ? data.data : item)));
            toast({
              variant: "success",
              title: recommendation.shortlisted ? "Removed from shortlist" : "Added to shortlist",
            });
          }
        } finally {
          setShortlistLoadingState((prev) => {
            const next = { ...prev };
            delete next[recommendation.id];
            return next;
          });
        }
      })();
    },
    [toast]
  );

  const handleToggleDismiss = useCallback(
    (recommendation: TCompanyRecommendation) => {
      setDismissLoadingState((prev) => ({ ...prev, [recommendation.id]: true }));

      void (async () => {
        try {
          // Dismissing removes the recommendation from the shortlist too.
          const nextDismissed = !recommendation.dismissed;
          const { data, error } = await dealNetworkService.updateCompanyRecommendation(recommendation.id, {
            dismissed: nextDismissed,
            shortlisted: nextDismissed ? false : recommendation.shortlisted,
          });

          if (error) {
            const message = await extractErrorMessage(error);
            toast({
              title: "Unable to update recommendation",
              description: message,
              variant: "destructive",
            });
            return;
          }

          if (data?.data) {
            setCompanyRecommendations((prev) => prev.map((item) => (item.id === data.data.id ? data.data : item)));
            toast({
              variant: "success",
              title: recommendation.dismissed ? "Recommendation restored" : "Recommendation dismissed",
            });
          }
        } finally {
          setDismissLoadingState((prev) => {
            const next = { ...prev };
            delete next[recommendation.id];
            return next;
          });
        }
      })();
    },
    [toast]
  );

  const handleRequestIntroduction = useCallback(
    (recommendation: TCompanyRecommendation) => {
      // The disabled={isRequestLoading} guard flushes with React state, so two
      // clicks in the same frame both fire the handler. Guard synchronously.
      if (inFlightConnectRequests.current.has(recommendation.id)) {
        return;
      }
      inFlightConnectRequests.current.add(recommendation.id);
      setConnectRequestLoadingState((prev) => ({ ...prev, [recommendation.id]: true }));

      void (async () => {
        try {
          const { data, error } = await dealNetworkService.createRecommendationConnectRequest(recommendation.id);

          if (error) {
            const message = await extractErrorMessage(error);
            toast({
              title: "Unable to request introduction",
              description: message,
              variant: "destructive",
            });
            return;
          }

          if (data?.data) {
            setCompanyRecommendations((prev) =>
              prev.map((item) => (item.id === recommendation.id ? { ...item, connect_request: data.data } : item))
            );
            toast({
              variant: "success",
              title: "Introduction requested",
              description: "Your introduction request has been sent.",
            });
          }
        } finally {
          inFlightConnectRequests.current.delete(recommendation.id);
          setConnectRequestLoadingState((prev) => {
            const next = { ...prev };
            delete next[recommendation.id];
            return next;
          });
        }
      })();
    },
    [toast]
  );

  const getInvestorDisplayName = useCallback((rec: TCompanyRecommendation) => {
    return (
      rec.investor_platform_investor?.contact_name ??
      rec.investor_platform_investor?.company_name ??
      rec.investor_company?.name ??
      ""
    );
  }, []);

  const sortedCompanyRecommendations = useMemo(() => {
    return [...companyRecommendations].sort((a, b) => {
      if (sortBy === "name") {
        return getInvestorDisplayName(a).localeCompare(getInvestorDisplayName(b));
      }
      const scoreA = typeof a.score === "number" ? a.score : -Infinity;
      const scoreB = typeof b.score === "number" ? b.score : -Infinity;
      return scoreB - scoreA;
    });
  }, [companyRecommendations, sortBy, getInvestorDisplayName]);

  const activeRecommendations = useMemo(
    () => sortedCompanyRecommendations.filter((rec) => !rec.dismissed),
    [sortedCompanyRecommendations]
  );
  const dismissedRecommendations = useMemo(
    () => sortedCompanyRecommendations.filter((rec) => rec.dismissed),
    [sortedCompanyRecommendations]
  );

  const filteredCompanyRecommendations = useMemo(() => {
    let result: TCompanyRecommendation[];
    switch (activeRecommendationFilter.type) {
      case "shortlisted":
        result = activeRecommendations.filter((rec) => rec.shortlisted);
        break;
      case "dismissed":
        result = dismissedRecommendations;
        break;
      case "categories": {
        const ids = activeRecommendationFilter.categoryIds;
        result = ids?.length
          ? activeRecommendations.filter((rec) =>
              rec.section_categories.some((category) => ids.includes(category.id))
            )
          : activeRecommendations;
        break;
      }
      case "all":
      default:
        result = activeRecommendations;
    }

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (rec) =>
          getInvestorDisplayName(rec).toLowerCase().includes(query) ||
          (rec.assessment ?? "").toLowerCase().includes(query)
      );
    }
    return result;
  }, [activeRecommendationFilter, activeRecommendations, dismissedRecommendations, searchQuery, getInvestorDisplayName]);

  // Reset to the first page whenever the visible set changes shape.
  useEffect(() => {
    setPage(1);
  }, [activeRecommendationFilter, searchQuery, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredCompanyRecommendations.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedRecommendations = useMemo(
    () => filteredCompanyRecommendations.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredCompanyRecommendations, currentPage]
  );
  const rangeStart = filteredCompanyRecommendations.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredCompanyRecommendations.length);

  const hasAnyCompanyRecommendations = companyRecommendations.length > 0;
  const showCompanyRecommendationsLoadingState = isCompanyRecommendationsLoading && !hasAnyCompanyRecommendations;
  const recommendationsUsageSummary = useMemo(() => {
    let totalCost = 0;
    let totalTokens = 0;
    let hasUsage = false;

    for (const recommendation of companyRecommendations) {
      const usage = extractUsageSummary(recommendation as unknown);
      if (!usage) continue;
      if (usage.totalCost !== null) totalCost += usage.totalCost;
      if (usage.totalTokens !== null) totalTokens += usage.totalTokens;
      hasUsage = true;
    }

    if (!hasUsage) return null;
    return { totalCost, totalTokens };
  }, [companyRecommendations]);

  const recommendationEmptyStateMessage = (() => {
    if (activeRecommendationFilter.type === "shortlisted") {
      return "You haven't shortlisted any investors yet.";
    }
    if (activeRecommendationFilter.type === "dismissed") {
      return "You haven't dismissed any investors yet.";
    }
    if (activeRecommendationFilter.type === "categories" && activeRecommendationFilter.categoryIds.length > 0) {
      return "No investors match the selected categories.";
    }
    return "We don't have investor matches for your company yet. Check back soon.";
  })();

  return (
    <div className="flex h-full flex-col gap-4">
      {companyId ? (
        <>
          {companyRecommendationsError ? (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>{companyRecommendationsError}</span>
            </div>
          ) : null}

          {!showCompanyRecommendationsLoadingState && hasAnyCompanyRecommendations ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p
                className="text-sm text-muted-foreground"
                aria-live="polite"
              >
                {filteredCompanyRecommendations.length === companyRecommendations.length
                  ? `${filteredCompanyRecommendations.length} investor ${filteredCompanyRecommendations.length === 1 ? "match" : "matches"}`
                  : `${filteredCompanyRecommendations.length} of ${companyRecommendations.length} investor matches`}
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search investors..."
                    aria-label="Search investors by name or match rationale"
                    className="h-9 w-full pl-9 sm:w-56"
                  />
                </div>
                <Select
                  value={sortBy}
                  onValueChange={(value) => setSortBy(value === "name" ? "name" : "score")}
                  options={[
                    { label: "Sort: Top match", value: "score" },
                    { label: "Sort: Name A–Z", value: "name" },
                  ]}
                  aria-label="Sort investor matches"
                  className="sm:w-44"
                />
              </div>
            </div>
          ) : null}

          {showCompanyRecommendationsLoadingState ? (
            <div className="space-y-4">
              <SectionCardSkeleton variant="dealNetwork" />
              <SectionCardSkeleton variant="dealNetwork" />
            </div>
          ) : filteredCompanyRecommendations.length > 0 ? (
            <>
              <div className="space-y-4">
                {paginatedRecommendations.map((recommendation) => (
                  <CompanyRecommendationCard
                    key={recommendation.id}
                    recommendation={recommendation}
                    companyId={companyId ?? null}
                    sectionId={sectionId ?? null}
                    onToggleShortlist={handleToggleShortlist}
                    onToggleDismiss={handleToggleDismiss}
                    onRequestIntroduction={handleRequestIntroduction}
                    isShortlistLoading={Boolean(shortlistLoadingState[recommendation.id])}
                    isDismissLoading={Boolean(dismissLoadingState[recommendation.id])}
                    isRequestLoading={Boolean(connectRequestLoadingState[recommendation.id])}
                  />
                ))}
              </div>

              {totalPages > 1 ? (
                <nav
                  className="flex items-center justify-between gap-2 border-t border-border pt-3"
                  aria-label="Recommendations pagination"
                >
                  <p className="text-xs text-muted-foreground">
                    Showing {rangeStart}–{rangeEnd} of {filteredCompanyRecommendations.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                      <Button
                        key={pageNumber}
                        size="sm"
                        variant={pageNumber === currentPage ? "default" : "outline"}
                        onClick={() => setPage(pageNumber)}
                        aria-label={`Page ${pageNumber}`}
                        aria-current={pageNumber === currentPage ? "page" : undefined}
                        className="min-w-9"
                      >
                        {pageNumber}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </nav>
              ) : null}

              {recommendationsUsageSummary ? (
                <p className="text-xs text-muted-foreground">
                  AI usage: {formatUsageCost(recommendationsUsageSummary.totalCost)} |{" "}
                  {formatUsageTokens(recommendationsUsageSummary.totalTokens)} tokens
                </p>
              ) : null}
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-border bg-muted p-8 text-center text-sm text-muted-foreground">
              {searchQuery.trim()
                ? `No investor matches found for "${searchQuery.trim()}". Try a different search.`
                : recommendationEmptyStateMessage}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
